/**
 * InputQuestion component
 *
 * Description:
 * Renders an open-ended question input for user comments or answers, handling state and answer submission.
 *
 * Features:
 * - Displays question and instructions.
 * - Provides a multiline text input for user response.
 * - Handles answer submission on blur event.
 * - Uses Material-UI for UI elements.
 */
import { useState } from 'react';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';

import TextField from '@mui/material/TextField';

import { saveUpdateAnswer } from 'store/lesson-actions';

import classes from './InputQuestion.module.css';

const InputQuestion = props => {
  const { t } = useTranslation(['lesson']);
  // Local state for input value
  const [value, setValue] = useState();
  /**
   * Handles change in the input field and triggers next question.
   */
  const handleChange = event => {
    props.nextQuestion();
    setValue(event.target.value);
  };

  /**
   * Sends the answer to the backend when input loses focus.
   */
  const sendAnswerHandler = () => {
    (async () => {
      await saveUpdateAnswer(
        props.idApplication,
        null,
        null,
        value,
        props.questionData.idQuestion
      );
    })();
  };

  return (
    <>
      {/* Custom prop: 'className' for question styling */}
      <p className={classes.question}>{t('additional_comments')}</p>
      <p className={classes.questionStrong}>
        {t('enter_your_answer')}
      </p>
      {/* Custom prop: 'className' for input styling, 'onBlur' for answer submission */}
      <TextField
        label={t('writes')}
        multiline
        rows={4}
        value={value}
        onChange={handleChange}
        className={classes.inputAnswer}
        onBlur={sendAnswerHandler}
      />
    </>
  );
};

InputQuestion.propTypes = {
  /**
   * Function to go to the next question
   */
  nextQuestion: PropTypes.func,
  /**
   * Application ID for answer submission
   */
  idApplication: PropTypes.number,
  /**
   * Current question index
   */
  currentQuestion: PropTypes.number,
  /**
   * Question data object containing question info
   */
  questionData: PropTypes.object
};

InputQuestion.defaultProps = {
  nextQuestion: undefined,
  idApplication: undefined,
  currentQuestion: undefined,
  questionData: {}
};

export default InputQuestion;
